import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/ndis')({
  component: NdisPage,
  head: () => ({
    meta: [
      { title: 'NDIS & disability supports — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English guide to the NDIS for Warren-Blackwood residents: eligibility, the local Area Coordinator for the south-west, registered providers in the electorate, and when to ask the MP for help.',
      },
      { property: 'og:title', content: 'NDIS & disability supports — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'How to access the NDIS in the south-west: eligibility, your Local Area Coordinator, providers, and when an MP can advocate for you.',
      },
    ],
  }),
});

const SW_PROVIDERS = [
  {
    name: 'Advocacy WA',
    role: 'Independent disability advocacy — helps with NDIS reviews, appeals, housing disputes.',
    contact: '(08) 9721 6444',
    href: 'https://advocacywa.org.au/',
  },
  {
    name: 'Enable WA',
    role: 'Therapy, support coordination, supported independent living.',
    contact: '1300 275 232',
    href: 'https://enablewa.org.au/',
  },
  {
    name: 'Nulsen Group',
    role: 'Supported accommodation and complex disability supports.',
    contact: '(08) 9344 1011',
    href: 'https://www.nulsen.com.au/',
  },
  {
    name: 'Activ',
    role: 'Day options, employment, accommodation across the south-west.',
    contact: '(08) 9387 0555',
    href: 'https://www.activ.asn.au/',
  },
  {
    name: 'Rocky Bay',
    role: 'Therapy, equipment, support services — services Bunbury hub covering the south-west.',
    contact: '(08) 6282 1900',
    href: 'https://www.rockybay.org.au/',
  },
  {
    name: 'Ability Centre',
    role: 'Therapy and supports, particularly for children and young people.',
    contact: '(08) 9443 0211',
    href: 'https://www.abilitycentre.com.au/',
  },
  {
    name: 'Carers WA',
    role: "Support and respite for unpaid family carers (not NDIS-funded but works alongside).",
    contact: '1300 227 377',
    href: 'https://www.carerswa.asn.au/',
  },
];

function NdisPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = {
    color: 'var(--green-mid)',
    fontWeight: 600,
  };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Disability &amp; NDIS
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        NDIS &amp; disability supports
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        Warren-Blackwood has an older population than the WA average and
        significant disability service need. This page is the plain-English
        starting point — what the NDIS is, how to check eligibility, who your
        Local Area Coordinator is, which providers operate in the south-west,
        and when an MP can help.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>Quick contacts</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          NDIS national line — <a href="tel:1800800110" style={link}>1800 800 110</a> (Mon–Fri 8am–8pm)
          <br />
          National Disability Abuse &amp; Neglect hotline — <a href="tel:1800880052" style={link}>1800 880 052</a>
          <br />
          Carer Gateway — <a href="tel:1800422737" style={link}>1800 422 737</a>
          <br />
          Disability Gateway — <a href="tel:1800643787" style={link}>1800 643 787</a> · <a href="https://www.disabilitygateway.gov.au/" style={link}>disabilitygateway.gov.au</a>
        </p>
      </div>

      <h2 style={h2}>What the NDIS is, in plain English</h2>
      <p style={p}>
        The National Disability Insurance Scheme (NDIS) is a Commonwealth-funded
        program that pays for supports and services that help people with a
        permanent and significant disability live their life. It is not
        means-tested. It is not Centrelink. It replaces the patchwork of
        state-based disability funding that came before it.
      </p>
      <p style={p}>
        If you are eligible, you get an individual <em>plan</em> with a budget
        attached. That budget pays for things like therapy, personal care,
        assistive technology, home modifications, transport, employment
        supports, and (for some participants) supported accommodation. You
        choose who delivers the supports — registered providers, unregistered
        providers, or in some cases family or community members.
      </p>
      <p style={p}>
        The scheme is run by the National Disability Insurance Agency (NDIA).
        Day-to-day, most people in the south-west deal with their Local Area
        Coordinator (LAC) rather than the NDIA directly.
      </p>

      <h2 style={h2}>Check your eligibility</h2>
      <p style={p}>To access the NDIS you generally need to:</p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          Be aged <strong>under 65</strong> when you first apply.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Be an Australian citizen, permanent resident, or Protected Special
          Category visa holder.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Have a <strong>permanent and significant disability</strong> that
          substantially affects your ability to do everyday activities — or be
          a child under 9 who would benefit from early intervention.
        </li>
      </ul>
      <p style={p}>
        <a
          href="https://www.ndis.gov.au/applying-access-ndis/am-i-eligible"
          style={link}
        >
          Use the NDIA's official eligibility checklist →
        </a>
      </p>
      <p style={p}>
        If you are <strong>over 65</strong> and not already on the NDIS, your
        equivalent pathway is <strong>My Aged Care</strong> —{' '}
        <a href="tel:1800200422" style={link}>1800 200 422</a> or{' '}
        <a href="https://www.myagedcare.gov.au/" style={link}>myagedcare.gov.au</a>.
      </p>

      <h2 style={h2}>Your Local Area Coordinator (LAC)</h2>
      <p style={p}>
        In the south-west of WA, Local Area Coordination is delivered by{' '}
        <strong>APM Communities</strong>. The LAC
        is who you actually meet with, who helps you apply, who walks you
        through your first plan, and who you go back to at review time.
      </p>
      <div style={callout('#eefaf0', '#bfe5c6')}>
        <h3 style={{ ...h3, color: 'var(--rivergum)', marginTop: 0 }}>
          APM Communities — South-West WA
        </h3>
        <p style={{ ...p, marginBottom: '0.4rem' }}>
          Covers all six shires in Warren-Blackwood (Augusta-Margaret River,
          Nannup, Manjimup, Bridgetown-Greenbushes, Boyup Brook, Denmark) plus
          neighbouring south-west LGAs.
        </p>
        <p style={{ ...p, marginBottom: 0 }}>
          Phone — <a href="tel:1800491198" style={link}>1800 491 198</a>
          <br />
          Email — <a href="mailto:lac@apm.net.au" style={link}>lac@apm.net.au</a>
          <br />
          Web — <a href="https://www.apmcommunities.com.au/" style={link}>apmcommunities.com.au</a>
          <br />
          Outreach visits to Manjimup, Bridgetown, Denmark and Margaret River
          are scheduled regularly — phone to find the next visit in your town.
        </p>
      </div>
      <p style={p}>
        For children <strong>under 9</strong>, the pathway is the Early
        Childhood Approach, delivered locally by{' '}
        <strong>Wanslea / EACH</strong> rather than APM —{' '}
        <a href="tel:1800010300" style={link}>1800 010 300</a> or{' '}
        <a
          href="https://www.ndis.gov.au/understanding/families-and-carers/early-childhood-approach-children-younger-9"
          style={link}
        >
          NDIS Early Childhood Approach
        </a>.
      </p>

      <h2 style={h2}>How to apply, step by step</h2>
      <ol style={{ ...p, paddingLeft: '1.4rem' }}>
        <li style={{ marginBottom: '0.5rem' }}>
          <strong>Gather evidence of your disability</strong> — diagnosis
          letters, functional assessments from a GP, OT, psychologist or
          specialist. The clearer the evidence about how the disability affects
          daily life, the smoother the application.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          <strong>Contact APM Communities or the NDIA</strong> on the numbers
          above to begin an Access Request. You can also lodge online via{' '}
          <a href="https://www.ndis.gov.au/applying-access-ndis/how-apply" style={link}>
            ndis.gov.au
          </a>.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          <strong>Planning meeting</strong> — once access is approved, you meet
          your LAC to build your first plan. Bring someone you trust. Have
          examples of what a typical week looks like and where the difficulty
          sits.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          <strong>Plan starts</strong> — you receive your funded supports and
          choose providers. You can self-manage, use a plan manager, or have
          the NDIA manage funds.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          <strong>Annual review</strong> — your plan is reviewed (usually
          yearly). If your needs change or your plan is cut, you can request a
          review of the decision within 3 months.
        </li>
      </ol>

      <h2 style={h2}>Providers operating in the electorate</h2>
      <p style={p}>
        Not an exhaustive list — there are hundreds of registered and
        unregistered providers. These are organisations with an established
        footprint in the south-west, including outreach to Warren-Blackwood
        towns.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem', listStyle: 'none' }}>
        {SW_PROVIDERS.map((pv) => (
          <li
            key={pv.name}
            style={{
              marginBottom: '0.8rem',
              paddingBottom: '0.7rem',
              borderBottom: '1px solid var(--rule)',
            }}
          >
            <a href={pv.href} style={link}>
              {pv.name} →
            </a>
            <br />
            <span style={{ color: 'var(--ink-mid)', fontSize: '0.92rem' }}>
              {pv.role}
            </span>
            <br />
            <span style={{ color: 'var(--ink)', fontSize: '0.92rem' }}>
              <a href={`tel:${pv.contact.replace(/[^\d+]/g, '')}`} style={link}>
                {pv.contact}
              </a>
            </span>
          </li>
        ))}
      </ul>
      <p style={p}>
        For a full search of registered providers in your postcode, use the{' '}
        <a
          href="https://www.ndis.gov.au/participants/working-providers/find-registered-provider/provider-finder"
          style={link}
        >
          NDIA Provider Finder →
        </a>
      </p>

      <h2 style={h2}>When the MP's office can help</h2>
      <p style={p}>
        The NDIS is a Commonwealth program, but a state MP can advocate where
        the state and federal systems intersect — and that's where most
        Warren-Blackwood constituents get stuck.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Plan reviews and appeals</strong> — escalating cases where
          funded supports have been cut without an obvious reason or where
          reviews are taking many months.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Specialist Disability Accommodation (SDA) and housing</strong>{' '}
          — where social housing, NDIS housing supports, and Department of
          Communities all interact.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Transport and access</strong> — taxi user subsidies, ACROD
          parking, school bus access, and disability access on state-controlled
          roads and buildings.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>WA Health interface</strong> — where therapy, hospital
          discharge or aged-care assessments stall and you need someone in the
          system to follow up.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Provider thin markets</strong> — when no provider in our
          region offers the supports your plan says you should have.
        </li>
      </ul>
      <p style={p}>
        If you are stuck, ring the electorate office —{' '}
        <Link to="/contact" style={link}>see the contact page</Link> for office
        hours and the best way to get an appointment.
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.ndis.gov.au/" style={link}>ndis.gov.au</a> — official scheme information.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.disabilitygateway.gov.au/" style={link}>disabilitygateway.gov.au</a> — Australian Government one-stop for disability information.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.apmcommunities.com.au/" style={link}>apmcommunities.com.au</a> — the LAC partner for South-West WA.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://advocacywa.org.au/" style={link}>advocacywa.org.au</a> — independent disability advocacy in WA.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.ndiscommission.gov.au/" style={link}>ndiscommission.gov.au</a> — to make a complaint about a provider.
        </li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        This page is a community summary of publicly-available information from
        the NDIA, APM Communities and listed providers. It is not official NDIS
        advice. Phone numbers, providers and the LAC partner change from time
        to time — confirm details on the official sites linked above. Spot
        something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20NDIS%20page%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
